import React, { useMemo } from "react";
import { CircularProgress } from "@mui/material";
import CommonTable, { HeadCell } from "src/business-types/grocery/components/CommonTable";
import { Order } from "src/shared/services/interfacev2";
import { splitDateTime } from "src/shared/components/dateTime";
import { useDateTimeFormatter } from "src/shared/configurations/hooks/useDateTimeFormatter";

interface OrderTableProps {
  orders: Order[];
  loading: boolean;
  error: string | null;
  page: number;
  rowsPerPage: number;
  totalRecords: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onOrderSelect: (order: Order) => void;
}

interface FormattedOrderRow {
  id: string;
  sno: number;
  CustomerName: string;
  Orderid: string;
  table_number: string;
  CustomerPhoneNo: string;
  OrderType: string;
  OrderStatus: JSX.Element | string;
  Ordertime: string;
  Finalamount: number | string;
}

/** ✅ Safely resolve a unique row id */
const getOrderRowId = (order: Order, index: number): string => {
  return order?._id?.$oid || order?.orders?.[0]?.orderId || `row-${index}`;
};

const OrderTable: React.FC<OrderTableProps> = ({ orders, loading, error, page, rowsPerPage, onOrderSelect }) => {

  const { formatTime } = useDateTimeFormatter();

  const headCells: HeadCell<FormattedOrderRow>[] = [
    { id: "sno", label: "S.No" },
    { id: "CustomerName", label: "Customer Name" },
    { id: "Orderid", label: "Order ID" },
    { id: "table_number", label: "Table Number" },
    { id: "CustomerPhoneNo", label: "Phone Number" },
    { id: "OrderType", label: "Order Type" },
    { id: "OrderStatus", label: "Status" },
    { id: "Ordertime", label: "Time" },
    { id: "Finalamount", label: "Total Price", align: "right" },
  ];

  const formattedData = useMemo<FormattedOrderRow[]>(() => {
    if (!Array.isArray(orders)) return [];

    return orders.map((order, index) => {
      const txn = order?.orders?.[0];
      const {time: orderTime } = splitDateTime(txn?.orderedOn?.$date ?? "");

      const customerName = [order?.customerDetails?.firstName, order?.customerDetails?.middleName, order?.customerDetails?.lastName].filter(Boolean).join(" ") || "—";

      return {
        id: getOrderRowId(order, index),
        sno: page * rowsPerPage + index + 1,
        CustomerName: customerName,
        Orderid: txn?.orderId || "—",
        table_number: txn?.tableNumber?.toString() || "—",
        CustomerPhoneNo: order?.customerDetails?.phones?.[0]?.phone || "—",
        OrderType: order?.orderDetails[0].orderType,
        OrderStatus: (
          <span
            className={`font-bold ${
              txn?.status?.toLowerCase() === "completed"
                ? "text-green-600"
                : txn?.status?.toLowerCase() === "pending"
                ? "text-yellow-600"
                : txn?.status?.toLowerCase() === "cancelled"
                ? "text-red-600"
                : "text-black"
            }`}
          >
            {txn?.status || "Unknown"}
          </span>
        ),

        Ordertime: (formatTime(orderTime)) || "—",

        Finalamount: txn?.finalAmount.toFixed(2) ?? "—",
      };
    });
  }, [formatTime, orders, page, rowsPerPage]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <CircularProgress />
        <span className="ml-3 text-gray-600">Loading orders...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 py-6 bg-red-50 mx-4 rounded-lg">
        <div className="font-semibold">Error Loading Orders</div>
        <div className="text-sm mt-1">{error}</div>
      </div>
    );
  }

  return (
    <div className="mt-1">
      <CommonTable<FormattedOrderRow>
        data={formattedData}
        headCells={headCells}
        title=""
        selectable={false}
        showToolbar={false}
        pagenation={false}
        pageSize={rowsPerPage}
        onRowClick={(id) => {
          const selectedOrder = orders.find((o, i) => o?._id?.$oid === id || o?.orders?.[0]?.orderId === id || `row-${i}` === id);

          if (selectedOrder) {
            onOrderSelect(selectedOrder);
          }
        }}
        className="rounded-lg border shadow-inner p-1 w-full bg-white"
      />
    </div>
  );
};

export default OrderTable;
